// frontend/src/pages/RecursosBodegaPage.jsx
import React, { useState, useEffect } from 'react';
import bodegaService from '../services/bodegaService';
import { recursoService } from '../services/onebaseService';
import Loading from '../components/Loading';
import './RecursosBodegaPage.css';

const RecursosBodegaPage = () => {
  const [recursos, setRecursos] = useState([]);
  const [recursosOnebase, setRecursosOnebase] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecurso, setEditingRecurso] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [formData, setFormData] = useState({
    recurso: '',
    codigo: '',
    categoria: '',
    unidad_medida: 'unidad',
    precio_costo_unitario: 0,
    stock_minimo: 0,
    status: 'activo'
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      await Promise.all([
        cargarRecursos(),
        cargarRecursosOnebase(),
        cargarCategorias()
      ]);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarRecursos = async () => {
    try {
      const response = await bodegaService.getRecursos();
      // La API devuelve un objeto paginado con la estructura: { count, next, previous, results }
      setRecursos(response.data.results || response.data);
    } catch (error) {
      console.error('Error al cargar recursos:', error);
    }
  };

  const cargarRecursosOnebase = async () => {
    try {
      const response = await recursoService.getAll({ stock: true });
      // La API devuelve un objeto paginado con la estructura: { count, next, previous, results }
      setRecursosOnebase(response.data.results || response.data);
    } catch (error) {
      console.error('Error al cargar recursos de onebase:', error);
    }
  };

  const cargarCategorias = async () => {
    try {
      const response = await bodegaService.getCategorias();
      // La API devuelve un objeto paginado con la estructura: { count, next, previous, results }
      setCategorias(response.data.results || response.data);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingRecurso) {
        await bodegaService.updateRecurso(editingRecurso.id, formData);
        alert('Recurso actualizado exitosamente');
      } else {
        await bodegaService.createRecurso(formData);
        alert('Recurso creado exitosamente');
      }

      setShowModal(false);
      setEditingRecurso(null);
      resetForm();
      cargarRecursos();
    } catch (error) {
      console.error('Error al guardar recurso:', error);
      alert(error.response?.data?.detail || 'Error al guardar recurso');
    }
  };

  const handleEdit = (recurso) => {
    setEditingRecurso(recurso);
    setFormData({
      recurso: recurso.recurso,
      codigo: recurso.codigo,
      categoria: recurso.categoria || '',
      unidad_medida: recurso.unidad_medida,
      precio_costo_unitario: recurso.precio_costo_unitario,
      stock_minimo: recurso.stock_minimo,
      status: recurso.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este recurso?')) {
      return;
    }

    try {
      await bodegaService.deleteRecurso(id);
      alert('Recurso eliminado exitosamente');
      cargarRecursos();
    } catch (error) {
      console.error('Error al eliminar recurso:', error);
      alert('Error al eliminar recurso');
    }
  };

  const resetForm = () => {
    setFormData({
      recurso: '',
      codigo: '',
      categoria: '',
      unidad_medida: 'unidad',
      precio_costo_unitario: 0,
      stock_minimo: 0,
      status: 'activo'
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRecurso(null);
    resetForm();
  };

  const recursosFiltrados = recursos.filter(recurso => {
    const matchSearch = !searchTerm ||
      recurso.recurso_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recurso.codigo?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchCategoria = !filterCategoria || recurso.categoria === parseInt(filterCategoria);

    return matchSearch && matchCategoria;
  });

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="recursos-page">
      <div className="page-header">
        <h1>📦 Recursos de Bodega</h1>
        <button
          className="btn-primary"
          onClick={() => setShowModal(true)}
        >
          ➕ Nuevo Recurso
        </button>
      </div>

      {/* Filtros */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder="🔍 Buscar por nombre o código..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={filterCategoria}
          onChange={(e) => setFilterCategoria(e.target.value)}
          className="filter-select"
        >
          <option value="">Todas las categorías</option>
          {categorias.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.nombre}</option>
          ))}
        </select>
      </div>

      {/* Tabla de recursos */}
      <div className="recursos-table-container">
        <table className="recursos-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Recurso</th>
              <th>Categoría</th>
              <th>Unidad</th>
              <th>Precio Costo</th>
              <th>Stock Mínimo</th>
              <th>Stock Total</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {recursosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="9" className="empty-row">
                  No hay recursos que coincidan con los filtros
                </td>
              </tr>
            ) : (
              recursosFiltrados.map((recurso) => (
                <tr key={recurso.id}>
                  <td><strong>{recurso.codigo}</strong></td>
                  <td>{recurso.recurso_nombre}</td>
                  <td>{recurso.categoria_nombre || '-'}</td>
                  <td>{recurso.unidad_medida}</td>
                  <td>${parseFloat(recurso.precio_costo_unitario).toLocaleString()}</td>
                  <td>{parseFloat(recurso.stock_minimo).toFixed(2)}</td>
                  <td>
                    <span className={recurso.stock_total < recurso.stock_minimo ? 'stock-bajo' : 'stock-ok'}>
                      {parseFloat(recurso.stock_total || 0).toFixed(2)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${recurso.status}`}>
                      {recurso.status === 'activo' ? '✅' : '❌'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(recurso)}
                      title="Editar"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(recurso.id)}
                      title="Eliminar"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal para crear/editar recurso */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRecurso ? '✏️ Editar Recurso' : '➕ Nuevo Recurso'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Recurso de OneBase *</label>
                  <select
                    value={formData.recurso}
                    onChange={(e) => setFormData({...formData, recurso: e.target.value})}
                    required
                    disabled={editingRecurso !== null}
                  >
                    <option value="">Seleccionar recurso...</option>
                    {recursosOnebase.map(rec => (
                      <option key={rec.id} value={rec.id}>
                        {rec.nombre} ({rec.unidad})
                      </option>
                    ))}
                  </select>
                  {editingRecurso && (
                    <small>No se puede cambiar el recurso después de creado</small>
                  )}
                </div>

                <div className="form-group">
                  <label>Código / SKU *</label>
                  <input
                    type="text"
                    value={formData.codigo}
                    onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                    required
                    placeholder="Ej: SKU-001"
                  />
                </div>

                <div className="form-group">
                  <label>Categoría</label>
                  <select
                    value={formData.categoria}
                    onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                  >
                    <option value="">Sin categoría</option>
                    {categorias.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Unidad de Medida *</label>
                  <select
                    value={formData.unidad_medida}
                    onChange={(e) => setFormData({...formData, unidad_medida: e.target.value})}
                    required
                  >
                    <option value="unidad">Unidad</option>
                    <option value="kg">Kilogramo</option>
                    <option value="gr">Gramo</option>
                    <option value="lt">Litro</option>
                    <option value="ml">Mililitro</option>
                    <option value="mt">Metro</option>
                    <option value="cm">Centímetro</option>
                    <option value="caja">Caja</option>
                    <option value="paquete">Paquete</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Precio Costo Unitario *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.precio_costo_unitario}
                    onChange={(e) => setFormData({...formData, precio_costo_unitario: e.target.value})}
                    required
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Stock Mínimo *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.stock_minimo}
                    onChange={(e) => setFormData({...formData, stock_minimo: e.target.value})}
                    required
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Estado</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingRecurso ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecursosBodegaPage;
